import {
  Edge,
  Node,
  addEdge,
  MiniMap,
  ReactFlow,
  OnConnect,
  Background,
  Connection,
  OnNodeDrag,
  OnReconnect,
  useReactFlow,
  useNodesState,
  useEdgesState,
  OnNodesChange,
  OnNodesDelete,
  OnEdgesDelete,
  IsValidConnection,
  ReactFlowInstance,
  SelectionDragHandler,
} from '@xyflow/react';
import { uniqBy } from 'lodash';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  nodeZIndex,
  APP_VERSION,
  PAPER_SIZES,
  RESOLUTION_SETTINGS,
  defaultConnectionData,
} from 'src/config';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { ImportConfigurationProvider } from 'src/context/import-configuration';
import { useUndoRedo } from 'src/context/undo-redo';
import { EEvents } from 'src/types/events';
import { ProjectJsonData } from 'src/types/project';
import { dispatchNodesChanged } from 'src/utils/dispatch-nodes-changed';
import { migrateProjectJsonData } from 'src/utils/migrate-project-json';
// eslint-disable-next-line import/order
import {
  exportFrame,
  exportImage,
  downloadImage,
  exportPdfFrameAsBlob,
} from 'src/utils/export-image';
import { getExportFileName } from 'src/utils/export-image-frame';
import {
  sortNodes,
  getNodeName,
  getInsideNodeId,
  getNodeIpWithSubnet,
  getNodePositionInsideParent,
  syncNodeIpInterfacesAndValidate,
} from 'src/utils/node';
import { sanitizePoEDevicesConnectedToNonPoEPorts } from 'src/utils/sanitize-poe';
import { validateNode } from 'src/utils/validate';
import { twMerge } from 'tailwind-merge';

import FlowControls from './controls';
import { FeGeConnectionNoticeDialog } from './dialog/fe-ge-connection-notice-dialog';
import edgeTypes from './edges/edge-types';
import './flow.scss';
import ImportConfigurationDialog from './import-configuration/import-configuration-dialog';
import FlowMenu from './menu';
import {
  isSwitchNode,
  isFeGePortMismatch,
  isPortConnectionAllowed,
  buildFeGeSwitchCableType,
  getPortConnectionTypeFromHandle,
} from './models/connection-utils';
import NodeData, {
  IEdgeData,
  EDataType,
  NodeDataType,
  EDataSwitchType,
  NodeCategoryModel,
} from './models/node-data';
import { AppNode, NodeModel } from './models/node-model';
import {
  nodeScale,
  getEdgeData,
  generateNodeId,
  getNodeIdFromHandle,
  getPortDataFromHandle,
  calculateEdgeAnimated,
} from './models/node-utils';
import nodeTypes from './nodes/node-types';
import SelectedNodesToolbar from './toolbar/selected-nodes-toolbar';

const zoomConfig = [0.1, 4];

const proOptions = {
  hideAttribution: true,
};

export default function Flow() {
  const {
    disableFlow,
    vlans,
    setVlans,
    globalIpSubnet,
    useCustomVlan,
    setGlobalIpSubnet,
    setUseCustomVlan,
    projectData,
    setProjectData,
    lastChanged,
    setLastChanged,
    globalSettings,
    setGlobalSettings,
  } = useGlobalContext();
  const { takeSnapshot, saveCurrentState } = useUndoRedo();

  const reactFlowWrapper = useRef<any>(null);
  const flowRef = useRef<any>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState<AppNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  const {
    screenToFlowPosition,
    getIntersectingNodes,
    getNodes,
    getEdges,
    fitView,
    getViewport,
  } = useReactFlow();
  const [isControlLoading, setIsControlLoading] = useState(false);
  const [isInteractive, setIsInteractive] = useState(true);
  const [feGeNoticeOpen, setFeGeNoticeOpen] = useState(false);
  const feGePendingConnectionRef = useRef<(() => void) | null>(null);

  // Single place for node validation: run when NODES_CHANGED or RELOAD_VALIDATE is dispatched
  useEffect(() => {
    const handleNodesChanged = (event: CustomEvent<{ nodeIds: string[] }>) => {
      const nodeIds = event.detail?.nodeIds ?? [];
      if (nodeIds.length === 0) return;
      const globalSubnet = useCustomVlan
        ? ''
        : (globalIpSubnet?.ipSubnet ?? '');
      setTimeout(() => {
        const currentNodes = getNodes();
        const currentEdges = getEdges();
        const updated = currentNodes.map((node) => {
          const category = node.data?.category as NodeCategoryModel;
          if (!category) return node;
          const result = validateNode(
            node.id,
            category,
            currentNodes as Node<NodeModel>[],
            currentEdges,
            globalSubnet,
            true,
            useCustomVlan ? undefined : globalIpSubnet?.subnetMask,
          );
          return {
            ...node,
            data: {
              ...node.data,
              category: {
                ...category,
                isValid: result.isValid,
                validationErrors: result.errorMessage,
              },
            },
          };
        });
        setNodes(updated as AppNode[]);
      }, 0);
    };
    window.addEventListener(
      EEvents.NODES_CHANGED,
      handleNodesChanged as EventListener,
    );
    window.addEventListener(
      EEvents.RELOAD_VALIDATE,
      handleNodesChanged as EventListener,
    );
    return () => {
      window.removeEventListener(
        EEvents.NODES_CHANGED,
        handleNodesChanged as EventListener,
      );
      window.removeEventListener(
        EEvents.RELOAD_VALIDATE,
        handleNodesChanged as EventListener,
      );
    };
  }, [getNodes, getEdges, setNodes, globalIpSubnet, useCustomVlan]);

  // Set up window.reactFlowInstance for E2E tests
  const onInit = useCallback(
    (reactFlowInstance: ReactFlowInstance<AppNode, Edge>) => {
      (window as any).reactFlowInstance = reactFlowInstance;
    },
    [],
  );

  const onAddEdge = useCallback(
    (params: Connection, oldEdge?: Edge) => {
      const sourceHandle = params.sourceHandle as string;
      const targetHandle = params.targetHandle as string;

      const edgeData = getEdgeData(sourceHandle) || ({} as IEdgeData);

      const sourceNodeId = getNodeIdFromHandle(sourceHandle);
      const targetNodeId = getNodeIdFromHandle(targetHandle);
      const sourceNode = nodes.find((node) => node.id === sourceNodeId);
      const targetNode = nodes.find((node) => node.id === targetNodeId);

      const sourceType = getPortConnectionTypeFromHandle(nodes, sourceHandle);
      const targetType = getPortConnectionTypeFromHandle(nodes, targetHandle);

      if (
        !isPortConnectionAllowed({
          sourceType,
          targetType,
          sourceNode,
          targetNode,
        })
      ) {
        return;
      }

      const isFeGeSwitch =
        isFeGePortMismatch(sourceType, targetType) &&
        isSwitchNode(sourceNode) &&
        isSwitchNode(targetNode);

      const isAnimate = calculateEdgeAnimated(
        sourceNodeId,
        targetNodeId,
        nodes,
      );

      const feGeCableType =
        isFeGeSwitch && sourceType && targetType
          ? buildFeGeSwitchCableType(sourceType, targetType)
          : undefined;

      const id = `${params.sourceHandle?.replace('__source', '')}____${params.targetHandle?.replace('__target', '')}`;

      // Snapshot is taken inside the apply step (before mutating) so undo always
      // restores the pre-connection topology, and edges displaced from an
      // occupied port are only removed once the connection is actually applied.
      const applyConnection = () => {
        takeSnapshot();
        setEdges((eds) => {
          const existingConnection = eds.filter(
            (edge) =>
              edge.sourceHandle === params.sourceHandle ||
              edge.targetHandle === params.targetHandle,
          );

          const existingConnectionData =
            existingConnection.length > 0 ? existingConnection[0].data : {};

          const hasExistingCableType =
            !!(existingConnectionData as { cableType?: unknown })?.cableType ||
            !!oldEdge?.data?.cableType;

          const edgeDataPayload = {
            ...defaultConnectionData,
            ...existingConnectionData,
            ...oldEdge?.data,
            ...(!hasExistingCableType && feGeCableType
              ? { cableType: feGeCableType }
              : {}),
          };

          const newEdge = {
            ...edgeData,
            ...params,
            animated: isAnimate,
            zIndex: 20,
            id,
            data: edgeDataPayload,
          } as Edge;

          const filteredEdges = eds.filter(
            (edge) => !existingConnection.includes(edge),
          );
          const newEdges = [newEdge, ...filteredEdges];
          syncNodeIpInterfacesAndValidate({
            newEdges,
            setNodes: setNodes as (
              payload: Node[] | ((nodes: Node[]) => Node[]),
            ) => void,
            vlans,
            ...(oldEdge ? { edgeRemoved: oldEdge } : {}),
            edgeId: id,
          });

          return addEdge(newEdge, filteredEdges);
        });
      };

      if (isFeGeSwitch) {
        feGePendingConnectionRef.current = applyConnection;
        setFeGeNoticeOpen(true);
        return;
      }

      applyConnection();
    },
    [nodes, setEdges, takeSnapshot, vlans, setNodes],
  );

  const handleFeGeNoticeConfirm = useCallback(() => {
    const apply = feGePendingConnectionRef.current;
    feGePendingConnectionRef.current = null;
    setFeGeNoticeOpen(false);
    apply?.();
  }, []);

  const handleFeGeNoticeCancel = useCallback(() => {
    feGePendingConnectionRef.current = null;
    setFeGeNoticeOpen(false);
  }, []);

  const onConnect: OnConnect = useCallback(
    (params: Connection) => {
      onAddEdge(params);
    },
    [onAddEdge],
  );

  const onReconnect: OnReconnect = useCallback(
    (oldEdge, newConnection) => {
      onAddEdge(newConnection, oldEdge);
    },
    [onAddEdge],
  );

  const isValidConnection = useCallback(
    (connection: Connection) => {
      const sourceHandle = connection?.sourceHandle as string;
      const targetHandle = connection?.targetHandle as string;

      const edgeData = getEdgeData(sourceHandle) || ({} as IEdgeData);
      const edgeDataTarget = getEdgeData(targetHandle) || ({} as IEdgeData);

      // Use actual node port data for PoE so custom/imported switches are validated correctly
      const sourcePortData = getPortDataFromHandle(nodes, sourceHandle);
      const targetPortData = getPortDataFromHandle(nodes, targetHandle);
      const sourcePortIsPoE = sourcePortData?.isPoE ?? edgeData?.isPoE;
      const targetPortIsPoE = targetPortData?.isPoE ?? edgeDataTarget?.isPoE;

      const nodeId = getNodeIdFromHandle(sourceHandle);
      const nodeIdTarget = getNodeIdFromHandle(targetHandle);

      const nodeData = nodes.find((node) => node.id === nodeId);
      const nodeDataEnabledPoE = nodeData?.data?.category?.poe?.enable;

      if (nodeDataEnabledPoE && !targetPortIsPoE) {
        return false;
      }
      const nodeDataTarget = nodes.find((node) => node.id === nodeIdTarget);
      const nodeDataTargetEnabledPoE =
        nodeDataTarget?.data?.category?.poe?.enable;

      if (nodeDataTargetEnabledPoE && !sourcePortIsPoE) {
        return false;
      }

      if (connection.source === connection.target) {
        return false;
      }

      const sourceType = getPortConnectionTypeFromHandle(nodes, sourceHandle);
      const targetType = getPortConnectionTypeFromHandle(nodes, targetHandle);

      return isPortConnectionAllowed({
        sourceType,
        targetType,
        sourceNode: nodeData,
        targetNode: nodeDataTarget,
      });
    },
    [nodes],
  ) as IsValidConnection;

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      const reactFlowBounds =
        reactFlowWrapper?.current?.getBoundingClientRect();
      const type = event.dataTransfer.getData(
        'application/reactflow',
      ) as NodeDataType;

      if (typeof type === 'undefined' || !type) {
        // eslint-disable-next-line no-console
        console.error('Invalid node type');
      }

      const nodeData = NodeData[type];
      const position = screenToFlowPosition({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const nodeDimensions = () => {
        if ((type as unknown as EDataType) === EDataType.Group)
          return { width: 400, height: 200 };
        if ((type as unknown as EDataType) === EDataType.Note)
          return { width: 100, height: 50 };
        if ((type as unknown as EDataType) === EDataType.Image)
          return { width: 200, height: 200 };
        if ((type as unknown as EDataType) === EDataType.ProjectFrame)
          return { width: PAPER_SIZES.A4.width, height: PAPER_SIZES.A4.height }; // A4 size at 96 DPI
        return {};
      };

      const id = `${type}-${generateNodeId()}`;

      const currentName =
        nodeData?.type === EDataType.Switch
          ? 'Switch'
          : nodeData?.name || 'Base Node';
      const isUnmanaged =
        nodeData?.type === EDataType.Switch &&
        nodeData?.switchType?.includes(EDataSwitchType.UNMANAGED);

      const hasGlobalIpSubnet = useCustomVlan ? '' : globalIpSubnet?.ipSubnet;

      const newNode: AppNode = {
        id,
        position,
        type,
        ...nodeDimensions(),
        ...(nodeData?.resizable && {
          width: nodeData.viewSize.width * nodeScale,
          height: nodeData.viewSize.height * nodeScale,
        }),
        data: {
          key: type,
          label: nodeData?.name,
          category: {
            ...nodeData,
            name: currentName,
            isValid: isUnmanaged,
          },
        },
        zIndex: nodeZIndex(type as unknown as EDataType),
      };

      const intersections = getIntersectingNodes({
        x: position.x,
        y: position.y,
        width: newNode.width ?? 40,
        height: newNode.height ?? 40,
      }).filter((n) => n.type === EDataType.Group);

      let groupNode: Node | undefined;
      if (intersections.length && newNode.type !== EDataType.Group) {
        const groupActiveId = getInsideNodeId(newNode, intersections);
        groupNode = intersections.find((n) => n.id === groupActiveId);
      }

      if (groupNode) {
        // if we drop a node on a group node, we want to position the node inside the group
        newNode.position = getNodePositionInsideParent(
          {
            position,
            width: newNode.width ?? 40,
            height: newNode.height ?? 40,
          },
          groupNode,
        ) ?? { x: 0, y: 0 };
        newNode.parentId = groupNode?.id;
        newNode.expandParent = true;
      }

      takeSnapshot();
      setNodes((nds) => {
        const isManaged =
          nodeData?.type === EDataType.Switch &&
          nodeData?.switchType?.includes(EDataSwitchType.MANAGED);
        newNode.data.category.name = getNodeName(nds, currentName);
        if (hasGlobalIpSubnet && isManaged) {
          const assignedIp = getNodeIpWithSubnet(nds, globalIpSubnet);
          if (assignedIp !== undefined) {
            newNode.data.category.ip = assignedIp;
            newNode.data.category.subnetMask = globalIpSubnet?.subnetMask;
            newNode.data.category.subnet = globalIpSubnet?.ipSubnet;
          } else {
            newNode.data.category.ip = '';
            newNode.data.category.subnetMask = globalIpSubnet?.subnetMask;
            newNode.data.category.subnet = globalIpSubnet?.ipSubnet;
          }
        }
        // we need to make sure that the parents are sorted before the children
        // to make sure that the children are rendered on top of the parents
        const sortedNodes = nds.concat(newNode).sort(sortNodes);
        return sortedNodes;
      });
    },
    [
      screenToFlowPosition,
      useCustomVlan,
      globalIpSubnet,
      getIntersectingNodes,
      takeSnapshot,
      setNodes,
    ],
  );

  const onNodeDragStop = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (node?.type === EDataType.Group) {
        return;
      }

      const intersections = getIntersectingNodes(node).filter(
        (n) => n.type === EDataType.Group,
      );
      let groupNode: Node | undefined;

      if (intersections.length) {
        const groupActiveId = getInsideNodeId(node, intersections);
        groupNode = intersections.find((n) => n.id === groupActiveId);
      }

      // when there is an intersection on drag stop, we want to attach the node to its new parent
      if (
        groupNode &&
        intersections.length &&
        node.parentId !== groupNode?.id
      ) {
        const nextNodes: Node[] = getNodes()
          .map((n) => {
            if (n.id === node.id && groupNode) {
              const position = getNodePositionInsideParent(n, groupNode) ?? {
                x: 0,
                y: 0,
              };

              return {
                ...n,
                position,
                className: '',
                parentId: groupNode.id,
                extent: 'parent',
              } as Node;
            }

            return {
              ...n,
              className: '',
            };
          })
          .sort(sortNodes);

        setNodes(nextNodes as AppNode[]);
      } else {
        setNodes((nds) => nds.map((n) => ({ ...n, className: '' })));
      }
    },
    [getIntersectingNodes, getNodes, setNodes],
  );

  const onNodeDrag = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (node?.type === EDataType.Group) {
        return;
      }

      const intersections = getIntersectingNodes(node).filter(
        (n) => n.type === EDataType.Group,
      );

      let groupActiveId = '';
      if (intersections.length) {
        // find intersection child with has min width
        groupActiveId = getInsideNodeId(node, intersections);
      }

      setNodes((nds) =>
        nds.map((n) => {
          if (
            groupActiveId &&
            n.type === EDataType.Group &&
            n.id === groupActiveId &&
            n.parentId !== groupActiveId
          ) {
            return {
              ...n,
              className: 'active',
            };
          }
          if (n.id === node.id) {
            return {
              ...n,
              ...(node as AppNode),
              className: '',
            };
          }

          return { ...n, className: '' };
        }),
      );
    },
    [getIntersectingNodes, setNodes],
  );

  const onNodeDragStart: OnNodeDrag = useCallback(() => {
    takeSnapshot();
  }, [takeSnapshot]);

  const onSelectionDragStart: SelectionDragHandler = useCallback(() => {
    takeSnapshot();
  }, [takeSnapshot]);

  const onNodesDelete: OnNodesDelete = useCallback(() => {
    takeSnapshot();
  }, [takeSnapshot]);

  const onEdgesDelete: OnEdgesDelete = useCallback(() => {
    takeSnapshot();
  }, [takeSnapshot]);

  useEffect(() => {
    saveCurrentState();
  }, [
    saveCurrentState,
    nodes,
    edges,
    vlans,
    globalIpSubnet,
    useCustomVlan,
    projectData,
    lastChanged,
    globalSettings,
    setLastChanged,
  ]);

  const onExportAsPng = useCallback(async () => {
    if (flowRef.current === null) return;
    try {
      setIsControlLoading(true);
      await downloadImage({
        element: flowRef.current,
        projectName: projectData?.projectName ?? 'network-configuration',
        pixelRatio: 4,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Image export error:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Error exporting image';
      alert(errorMessage);
      // eslint-disable-next-line no-console
      alert('Error exporting as PNG');
    } finally {
      setIsControlLoading(false);
    }
  }, [projectData?.projectName]);

  const prepareFrameExportParams = useCallback(async () => {
    const frameNode = nodes.find(
      (node) => node.type === EDataType.ProjectFrame,
    );
    if (!frameNode || flowRef.current === null) return null;

    fitView?.({ nodes: [frameNode], padding: 0.4 });
    await new Promise((resolve) => setTimeout(resolve, 100));

    const viewport = getViewport();
    const projectName = projectData?.projectName ?? 'network-configuration';
    const revision = projectData?.revisionNumber ?? '';
    const fileName = getExportFileName(projectName, revision);

    return {
      element: flowRef.current,
      frameObject: {
        positionX: frameNode.position.x * viewport.zoom + viewport.x,
        positionY: frameNode.position.y * viewport.zoom + viewport.y,
        width: (frameNode.width ?? 0) * viewport.zoom,
        height: (frameNode.height ?? 0) * viewport.zoom,
      },
      projectName,
      paperSize: projectData?.exportSettings?.paperSize ?? 'A4',
      orientation: projectData?.exportSettings?.frameOrientation ?? 'portrait',
      pixelRatio:
        RESOLUTION_SETTINGS[projectData?.exportSettings?.resolution ?? 'medium']
          .pixelRatio,
      fileName: fileName || undefined,
    };
  }, [
    projectData?.projectName,
    projectData?.revisionNumber,
    projectData?.exportSettings,
    nodes,
    fitView,
    getViewport,
  ]);

  const onExportFrame = useCallback(
    async (type: 'pdf') => {
      const frameNode = nodes.find(
        (node) => node.type === EDataType.ProjectFrame,
      );
      if (!frameNode) {
        alert('Project frame not found');
        return;
      }
      const params = await prepareFrameExportParams();
      if (!params) return;

      try {
        setIsControlLoading(true);
        await exportFrame(type, params);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Export error:', error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : `Error exporting as ${type.toUpperCase()}`;
        alert(errorMessage);
      } finally {
        setIsControlLoading(false);
      }
    },
    [nodes, prepareFrameExportParams],
  );

  const onGetFrameBlob = useCallback(async (): Promise<{
    blob: Blob;
    filename: string;
  } | null> => {
    const params = await prepareFrameExportParams();
    if (!params) return null;
    return exportPdfFrameAsBlob(params);
  }, [prepareFrameExportParams]);

  const onGetScreenshotBlob = useCallback(async (): Promise<{
    blob: Blob;
    filename: string;
  } | null> => {
    if (flowRef.current === null) return null;
    const dataUrl = await exportImage({
      element: flowRef.current,
      pixelRatio: 4,
    });
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const name = projectData?.projectName ?? 'network-configuration';
    return { blob, filename: `${name}_Screenshot.png` };
  }, [projectData?.projectName]);

  const saveDataAsJson = useCallback(() => {
    const data: ProjectJsonData = {
      version: APP_VERSION,
      nodes,
      edges,
      vlans,
      globalIpSubnet,
      useCustomVlan,
      projectData,
      lastChanged,
      globalSettings,
    };
    return data;
  }, [
    nodes,
    vlans,
    edges,
    globalIpSubnet,
    useCustomVlan,
    projectData,
    lastChanged,
    globalSettings,
  ]);

  const applyDataFromJson = useCallback(
    (rawData: ProjectJsonData) => {
      const data = migrateProjectJsonData(rawData);

      if (data.version !== APP_VERSION) {
        const isConfirm = window.confirm(
          'This file is not compatible with the current version of the application. Do you want to continue?',
        );
        if (!isConfirm) return;
      }

      if (!data || !data.nodes || !Array.isArray(data.nodes)) {
        alert('Invalid JSON');
        return;
      }
      takeSnapshot();
      const allAvailableNodeIds = [...nodes, ...data.nodes].map(
        (node) => node.id,
      );
      // Merged edges for sanitize: devices with PoE on non-PoE port get poe.enable turned off
      const mergedEdgesForSanitize = uniqBy(
        [...getEdges(), ...(data.edges || [])],
        'id',
      ).filter(
        (edge) =>
          allAvailableNodeIds.includes(edge.source) &&
          allAvailableNodeIds.includes(edge.target),
      );

      // Compute sanitized nodes first so edge.animated uses updated poe.enable
      let nodesForEdges = uniqBy([...nodes, ...data.nodes], 'id');
      if (data.nodes && data.nodes.length > 0) {
        const merged = uniqBy(
          [
            ...getNodes(),
            ...data.nodes.map((node: Node<NodeModel>) => ({
              ...node,
              data: node.data,
            })),
          ],
          'id',
        );
        const withZIndex = merged.map((node) => ({
          ...node,
          zIndex: nodeZIndex(node.type as unknown as EDataType),
        })) as AppNode[];
        nodesForEdges = sanitizePoEDevicesConnectedToNonPoEPorts(
          withZIndex,
          mergedEdgesForSanitize,
        );
        const importedIds = data.nodes.map((n: Node<NodeModel>) => n.id);
        setNodes(nodesForEdges);
        dispatchNodesChanged(importedIds);
      }
      if (data.edges && data.edges.length > 0) {
        const allNodes = nodesForEdges;
        const importedEdgeById = new Map<string, Edge>(
          data.edges.map((edge: Edge) => [edge.id, edge]),
        );

        setEdges((prevEdges) => {
          const newEdges = uniqBy([...prevEdges, ...data.edges], 'id').filter(
            (edge) =>
              allAvailableNodeIds.includes(edge.source) &&
              allAvailableNodeIds.includes(edge.target),
          );

          return newEdges.map((edge) => {
            const isAnimate = calculateEdgeAnimated(
              edge.source,
              edge.target,
              allNodes,
            );

            const importedEdge = importedEdgeById.get(edge.id);
            return {
              ...edge,
              animated: !!isAnimate,
              data: {
                ...edge.data,
                ...(importedEdge?.data ?? {}),
              },
            };
          });
        });
      }
      if (data.vlans && data.vlans.length > 0) {
        setVlans(data.vlans);
      }
      if (data.globalIpSubnet) {
        setGlobalIpSubnet(data.globalIpSubnet);
      }
      if (data.useCustomVlan !== undefined) {
        setUseCustomVlan(data.useCustomVlan);
      }
      if (data.projectData) {
        setProjectData(data.projectData);
      }
      if (data.lastChanged) {
        setLastChanged(data.lastChanged);
      }
      if (data.globalSettings) {
        setGlobalSettings(data.globalSettings);
      }
    },
    [
      setNodes,
      setVlans,
      setEdges,
      setGlobalIpSubnet,
      setUseCustomVlan,
      setProjectData,
      takeSnapshot,
      setLastChanged,
      setGlobalSettings,
      getEdges,
      getNodes,
      nodes,
    ],
  );

  const onExportAsJson = useCallback(async () => {
    try {
      const data = saveDataAsJson();
      const dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(
        JSON.stringify(data, null, 2),
      )}`;
      const a = document.createElement('a');
      a.setAttribute('download', `${projectData?.projectName}.json`);
      a.setAttribute('href', dataStr);
      a.click();
    } catch (error) {
      // eslint-disable-next-line no-console
      alert('Error exporting as JSON');
    }
  }, [saveDataAsJson, projectData]);

  const onImportJson = useCallback(() => {
    try {
      const input = document.createElement('input');
      // only accept json files
      input.type = 'file';
      input.accept = '.json';
      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
          const data = JSON.parse(event.target?.result as string);
          applyDataFromJson(data);
        };
        reader.readAsText(file);
      };
      input.click();
    } catch (error) {
      // eslint-disable-next-line no-console
      alert('Error importing JSON');
    }
  }, [applyDataFromJson]);

  return (
    <>
      <div
        className={twMerge([
          'h-full flex-grow',
          disableFlow ? 'grayscale' : '',
        ])}
        ref={reactFlowWrapper}
      >
        <ReactFlow
          id="react-flow-container"
          ref={flowRef}
          nodes={nodes}
          nodeTypes={nodeTypes}
          edges={edges}
          edgeTypes={edgeTypes}
          onInit={onInit}
          onNodesChange={onNodesChange as OnNodesChange<Node>}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onReconnect={onReconnect}
          onDrop={onDrop}
          onNodeDrag={onNodeDrag}
          onNodeDragStop={onNodeDragStop}
          onNodeDragStart={onNodeDragStart}
          onSelectionDragStart={onSelectionDragStart}
          onNodesDelete={onNodesDelete}
          onEdgesDelete={onEdgesDelete}
          maxZoom={zoomConfig[1]}
          minZoom={zoomConfig[0]}
          isValidConnection={isValidConnection}
          onDragOver={onDragOver}
          proOptions={proOptions}
          //
          edgesFocusable={isInteractive && !disableFlow}
          nodesDraggable={isInteractive && !disableFlow}
          nodesConnectable={isInteractive && !disableFlow}
          nodesFocusable={isInteractive && !disableFlow}
          elementsSelectable={isInteractive && !disableFlow}
          // Optional if you also want to lock zooming
          zoomOnDoubleClick={false}
          zoomOnScroll={!disableFlow}
        >
          {/* <Controls>
          <ControlButton
            title="Export as PNG"
            disabled={nodes.length === 0 || isControlLoading}
            onClick={onExportAsPng}
          >
            {isControlLoading ? (
              <LoadingIcon className="h-6 w-6" />
            ) : (
              <PhotoIcon className="h-6 w-6" />
            )}
          </ControlButton>
          <ControlButton
            title="Export as JSON"
            disabled={nodes.length === 0 || isControlLoading}
            onClick={onExportAsJson}
          >
            <ArrowDownTrayIcon className="h-6 w-6" />
          </ControlButton>

          <ControlButton
            disabled={isControlLoading}
            title="Import JSON"
            onClick={onImportJson}
          >
            <ArrowUpTrayIcon className="h-6 w-6" />
          </ControlButton>
        </Controls> */}
          <MiniMap position="bottom-left" />
          <Background />
          <SelectedNodesToolbar />
        </ReactFlow>
      </div>
      <ImportConfigurationProvider onApplyImport={applyDataFromJson}>
        <FlowMenu
          onExportFrame={onExportFrame}
          onExportScreenshot={onExportAsPng}
          onGetFrameBlob={onGetFrameBlob}
          onGetScreenshotBlob={onGetScreenshotBlob}
          disabledExport={nodes.length === 0 || isControlLoading}
          onExport={onExportAsJson}
          disabledImport={isControlLoading}
          onImport={onImportJson}
          isInteractive={isInteractive}
          setIsInteractive={setIsInteractive}
        />
        <ImportConfigurationDialog />
      </ImportConfigurationProvider>
      <FeGeConnectionNoticeDialog
        open={feGeNoticeOpen}
        onConfirm={handleFeGeNoticeConfirm}
        onCancel={handleFeGeNoticeCancel}
      />
      <FlowControls />
    </>
  );
}
